<template>
  <base-draggable-modal
      :name="modal_name"
      max-height="80vh"
      width="50vw"
      :fullscreen="$vuetify.display.xsOnly"
      :ref="modal_name">
    <v-card rounded="xl">
      <base-modal>
        <template #header>
          <v-toolbar class="draggable_selector" flat color="secondary" density="compact">
            <v-toolbar-title v-if="label" class="text-primary">{{ label }}</v-toolbar-title>
            <v-toolbar-title v-else class="text-primary">{{ $store.getters.translate(page.model) }}</v-toolbar-title>
            <v-spacer/>
            <base-action-button v-if="canDelete" class="mr-5" :delete="true" @action="archive">
              <template #prepend><PhArchive style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
              {{ $store.getters.translate("delete") }}
            </base-action-button>
          </v-toolbar>
        </template>
        <template #content>
            <v-card-text>
              <v-row v-if="logo_required && logo">
                <v-col>
                  <v-layout align-center justify-center column fill-height>
                    <v-img max-width="150" max-height="150" :src="logo" class="ma-auto"/>
                  </v-layout>
                </v-col>
              </v-row>
              <v-row v-if="(page.name === 'measurements' || page.name === 'defects') && record.article_id">
                <v-col>
                  <base-link-button @action="$router.push('/articles/' + record.article_id)">
                    <template #prepend><PhArrowSquareOut style="vertical-align: middle; margin-right: 5px;" size="20"/></template>
                    {{ $store.getters.translate("article") }}
                  </base-link-button>
                </v-col>
              </v-row>
              <!--USUAL FIELDS-->
              <template v-for="field in usual_fields">
                <!--FIELDSET OPTIONS FIELD-->
                <v-combobox
                    v-if="page.name === 'fieldsets' && field.name === 'alternates'"
                    density="default"
                    class="reg-normal text-primary mt-3"
                    base-color="border-tertiary"
                    bg-color="secondary"
                    color="primary"
                    :isDisabled="!canEdit"
                    v-model="record.alternates"
                    chips
                    closable-chips
                    hide-details
                    clearable
                    multiple>
                  <template #label>
                    <span class="sm-normal text-subtle">{{ $store.getters.translate('alternates') }}</span>
                  </template>
                </v-combobox>
                <!--FIELDSET OPTIONS FIELD-->
                <v-combobox
                    v-else-if="page.name === 'fieldsets' && record.type === 'enum' && record.options_source === 'custom_options' && field.name === 'options'"
                    density="default"
                    class="reg-normal text-primary mt-3"
                    base-color="border-tertiary"
                    bg-color="secondary"
                    color="primary"
                    :isDisabled="!canEdit"
                    v-model="record.options"
                    chips
                    closable-chips
                    hide-details
                    clearable
                    multiple>
                  <template #label>
                    <span class="sm-normal text-subtle">{{ $store.getters.translate('options') }}<strong>*</strong></span>
                  </template>
                </v-combobox>
                <base-field
                    v-else-if="customModelConditions(field.name)"
                    :key="field.name"
                    :isDisabled="!canEdit"
                    :modelValue="record[field.name]"
                    :field="field"
                    :create="canCreateNewModel(field.name)"
                    @change="usualFieldDataChanged"
                    @create="createNewModel"/>
              </template>
              <!--CUSTOM FIELDS-->
              <base-field
                  v-if="record.custom_fields"
                  v-for="field in custom_fields"
                  :key="field.name"
                  :isDisabled="!canEdit"
                  :modelValue="record.custom_fields[field.name]"
                  :field="field"
                  @change="customFieldDataChanged"/>
              <!--LIST FILES-->
              <v-row v-if="files_required" class="mt-5">
                <v-col>
                  <base-list-files
                      :page="page"
                      :parent_id="record.id"
                      :label="$store.getters.translate('files')"
                      :files="record.files"
                      :multiple_files="true"
                      :deletable="canEdit"
                      :create="canEdit"
                      @refresh="load(record.id)"/>
                </v-col>
              </v-row>
              <!--LIST LINKS-->
              <v-row v-if="links_required" class="mt-5">
                <v-col>
                  <base-list-links ref="base_link_list" :model_type="page.model" :model_id="record.id" :label="$store.getters.translate('links_to_other_systems')" :preview="preview"/>
                </v-col>
              </v-row>
              <!--TAGS-->
              <base-tags v-if="tags_required" :isDisabled="!canEdit" :available_tags="available_tags" :modelValue="record.tags" :modal_name="page.name" @change="tagsChanged"/>
            </v-card-text>
        </template>
        <template #footer>
          <v-card-text>
            <v-row>
              <v-col>
                <base-action-button :width="'100%'" @action="closeModal(modal_name)">
                  <template #prepend><PhX style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
                  {{ $store.getters.translate("cancel") }}
                </base-action-button>
              </v-col>
              <v-col v-if="canEdit">
                <base-action-button :background="true" :width="'100%'" @action="save(true)">
                  <template #prepend><PhFloppyDisk style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
                  {{ $store.getters.translate("save") }}
                </base-action-button>
              </v-col>
            </v-row>
          </v-card-text>
        </template>
      </base-modal>
    </v-card>
  </base-draggable-modal>
</template>

<script>
import { useAbility } from '@casl/vue';
import helpFunctions from "../../plugins/helpFunctions";
import BaseModal from "../../components/commonComponents/BaseModal";
import BaseDraggableModal from "../../components/commonComponents/BaseDraggableModal";
import BaseTags from "./BaseTags";
import BaseListFiles from "./BaseListFiles";
import BaseListLinks from "./BaseListLinks";
import BaseActionButton from "./BaseActionButton";
import BaseLinkButton from "./BaseLinkButton";
import {PhArchive, PhArrowSquareOut, PhFloppyDisk, PhX} from "@phosphor-icons/vue";

export default {
  props: ["modal_name", "page", "label", "files_required", "multiple_files", "links_required", "available_tags", "logo_required", "internal_model", "preview", "custom_permission_can_edit", "custom_permission_can_delete"],
  components: {
    BaseLinkButton,
    BaseModal,
    BaseDraggableModal,
    BaseTags,
    BaseListFiles,
    BaseListLinks,
    BaseActionButton,
    PhArrowSquareOut, PhArchive, PhFloppyDisk, PhX,
  },
  setup() {
    const { can } = new useAbility();
    return { can }
  },
  data() {
    return {
      record: {},
      usual_fields: [],
      custom_fields: [],
      tags_required: false,
      //APPLICATION VARIABLES. NOT USED FOR OTHER MODELS
      application_equipment_group: null,
      application_equipment_category: null,
      application_explosive_atmosphere: null,
      //MARKERING VARIABLES. NOT USED FOR OTHER MODELS
      regulation: null,
      gas_group: null,
      protection_method: null,
      temperature_class: null,
      equipment_protection_level: null,
      logo: null
    };
  },
  methods: {
    ...helpFunctions.modal_functions,
    async load(id) {
      this.usual_fields = [];
      this.custom_fields = [];
      this.logo = null;
      this.openModal(this.modal_name);
      this.record = await helpFunctions.loadModel(this.$toast, this.page.name, id);
      await this.getFields();
      this.checkLogo();
      if(this.page.model === 'tag') {
        this.record.name = this.record.name.en;
      }
    },
    async save(reload) {
      if (helpFunctions.modelFieldsFilled(this.$toast, this.record, this.usual_fields)) {
        if((this.page.model === 'projectmanagementanswer' || this.page.model === 'externallink') && this.record.url && !this.record.url.includes("http")) {
          this.$toast.error(this.$store.getters.translate("incorrect_website_format"));
        }
        else if(await helpFunctions.saveModel(this.$toast, this.page.name, this.record)) {
          if(this.page.model === 'translation') {
            await this.$store.dispatch("refreshTranslations");
          }
          if (this.page.model === 'projectmanagementanswer') {
            //CREATE EXTERNAL LINK IF ANSWER CONTAINS URL
            if (this.record.url) {
              await helpFunctions.createModel(this.$toast, helpFunctions.pages.externallinks.name, {
                model_id: this.record.project_management_id,
                model_type: 'projectmanagement',
                name: this.record.name,
                url: this.record.url,
              });
            }
          }
          if (reload) {
            this.$emit('refresh');
            this.closeModal(this.modal_name);
          }
        }
      }
    },
    async archive() {
      this.$confirm(this.$store.getters.translate("delete_confirmation")).then(async (res) => {
        if (res) {
          await helpFunctions.deleteModel(this.$toast, this.page.name, this.record.id);
          this.$emit('refresh');
          this.closeModal(this.modal_name);
        }
      });
    },
    checkLogo() {
      if(this.logo_required && this.record.files) {
        for (let i = 0; i < this.record.files.length; i++) {
          if (this.record.files[i].content_type === "image/png" || this.record.files[i].content_type === "image/jpeg") {
            this.logo = this.record.files[i].full_path;
            break;
          }
        }
      }
    },
    customModelConditions(field_name) {
      if(this.page.model === 'phase') {
        if(field_name === 'shutdown_required') {
          return (this.record.type === 'Construction' || this.record.type === 'Commissioning');
        }
        else if(field_name === 'shutdown_required_text' || field_name === 'shutdown_required_start_date' || field_name === 'shutdown_required_complete_date') {
          return ((this.record.type === 'Construction' || this.record.type === 'Commissioning') && this.record.shutdown_required === 'Yes');
        }
      }
      else if(this.page.model === 'fieldset') {
        if(field_name === 'options_source') {
          return this.record.type === 'enum';
        }
        else if(field_name === 'options' || field_name === 'alternates') {
          return false;
        }
      }
      //DEFAULT
      return true;
    },
    createNewModel(field_name) {
      let data = { modal_name: this.modal_name, field_name: field_name };
      this.$emit('createNewModel', data);
    },
    canCreateNewModel(field_name) {
      //PREVIOUSLY WAS USED FOR ARTICLE ITEMS BUT AS THEY WERE CONVERTED IN ARTICLES NO NEED THIS FUNCTION NOW.
      //BUT COULD BE USED LATER FOR OTHER MODELS.
      return false;
    },
    usualFieldDataChanged(data) {
      if(this.record[data[0]] !== data[1]) {
        this.record[data[0]] = data[1];
        if (this.page.model === 'application' && (data[0] === 'equipment_group_id' || data[0] === 'equipment_category_id' || data[0] === 'explosive_atmosphere_id')) {
          if (data[0] === 'equipment_group_id') {
            if (data[2]) {
              this.application_equipment_group = data[2].split(" | ")[0]; //data[2] stores selected text value
            }
            else {
              this.application_equipment_group = null;
            }
          }
          else if (data[0] === 'equipment_category_id') {
            if (data[2]) {
              this.application_equipment_category = data[2].split(" | ")[0]; //data[2] stores selected text value
            }
            else {
              this.application_equipment_category = null;
            }
          }
          else if (data[0] === 'explosive_atmosphere_id') {
            if (data[2]) {
              this.application_explosive_atmosphere = data[2].split(" | ")[0]; //data[2] stores selected text value
            }
            else {
              this.application_explosive_atmosphere = null;
            }
          }
          this.record.name = "";
          if (this.application_equipment_group) {
            this.record.name = this.application_equipment_group + " ";
          }
          if (this.application_equipment_category) {
            this.record.name += this.application_equipment_category + " ";
          }
          if (this.application_explosive_atmosphere) {
            this.record.name += this.application_explosive_atmosphere;
          }
        }
        else if (this.page.model === 'markering' && (data[0] === 'regulation_id' || data[0] === 'protection_method_id' || data[0] === 'gas_group_id' || data[0] === 'temperature_class_id' || data[0] === 'equipment_protection_level_id')) {
          if (data[0] === 'regulation_id') {
            if (data[2]) {
              this.regulation = data[2].split(" | ")[0]; //data[2] stores selected text value
            } else {
              this.regulation = null;
            }
          } else if (data[0] === 'protection_method_id') {
            if (data[2]) {
              this.protection_method = data[2].split(" | ")[0]; //data[2] stores selected text value
            } else {
              this.protection_method = null;
            }
          } else if (data[0] === 'gas_group_id') {
            if (data[2]) {
              this.gas_group = data[2].split(" | ")[0]; //data[2] stores selected text value
            } else {
              this.gas_group = null;
            }
          } else if (data[0] === 'temperature_class_id') {
            if (data[2]) {
              this.temperature_class = data[2].split(" | ")[0]; //data[2] stores selected text value
              this.record.maximum_surface_temperature = data[2];
            } else {
              this.temperature_class = null;
              this.record.maximum_surface_temperature = '';
            }
          } else if (data[0] === 'equipment_protection_level_id') {
            if (data[2]) {
              this.equipment_protection_level = data[2].split(" | ")[0]; //data[2] stores selected text value
            } else {
              this.equipment_protection_level = null;
            }
          }
          this.record.name = "";
          if (this.regulation) {
            this.record.name = this.regulation + " ";
          }
          if (this.protection_method) {
            this.record.name += this.protection_method + " ";
          }
          if (this.gas_group) {
            this.record.name += this.gas_group + " ";
          }
          if (this.temperature_class) {
            this.record.name += this.temperature_class + " ";
          }
          if (this.equipment_protection_level) {
            this.record.name += this.equipment_protection_level;
          }
        }
      }
    },
    customFieldDataChanged(data) {
      this.record.custom_fields[data[0]] = data[1];
    },
    tagsChanged(data) {
      this.record.tags = data;
    },
    async getFields() {
      const response = await helpFunctions.getFields(this.page.model, "show_on_update");
      this.tags_required = response.tags_required;
      this.usual_fields = response.usual_fields;
      this.custom_fields = response.custom_fields;
    },
  },
  computed: {
    canEdit() {
      return !this.preview && (this.internal_model || this.can("edit", this.$createEntity(this.page.model.toLowerCase(), { id: 0 })) ||
          this.can("edit", this.$createEntity(this.page.model.toLowerCase(), { id: this.record.id })) || this.custom_permission_can_edit);
    },
    canDelete() {
      return !this.preview && (this.internal_model || this.can("delete", this.$createEntity(this.page.model.toLowerCase(), { id: 0 })) ||
          this.can("delete", this.$createEntity(this.page.model.toLowerCase(), { id: this.record.id })) || this.custom_permission_can_delete);
    },
  },
};
</script>

<style scoped>
.article-img {
  max-height: 80px;
  max-width: 80px;
}
</style>